/**
 * Comprehensive i18n (Internationalization) for U-DORM
 * Supports: English, Tagalog, Spanish, French, Hindi
 */

export const translations = {
  en: {
    // Common
    common: {
      save: 'Save',
      cancel: 'Cancel',
      delete: 'Delete',
      edit: 'Edit',
      add: 'Add',
      search: 'Search',
      filter: 'Filter',
      loading: 'Loading...',
      noData: 'No data available',
      confirm: 'Confirm',
      back: 'Back',
      next: 'Next',
      submit: 'Submit',
      close: 'Close',
      view: 'View',
      download: 'Download',
      upload: 'Upload',
      copy: 'Copy',
      copied: 'Copied',
      success: 'Success',
      error: 'Error',
      warning: 'Warning',
      info: 'Information',
      yes: 'Yes',
      no: 'No',
      all: 'All',
      active: 'Active',
      pending: 'Pending',
      verified: 'Verified',
      rejected: 'Rejected',
      expired: 'Expired',
      today: 'Today',
      yesterday: 'Yesterday',
      welcome: 'Welcome',
      home: 'Home',
      about: 'About',
      contact: 'Contact',
      help: 'Help',
      required: 'Required',
      optional: 'Optional',
      actions: 'Actions',
      details: 'Details',
      status: 'Status',
      date: 'Date',
      time: 'Time',
      amount: 'Amount',
      total: 'Total',
      notes: 'Notes',
      description: 'Description',
      name: 'Name',
      email: 'Email',
      phone: 'Phone',
      address: 'Address',
      selectOption: 'Select an option',
      noResults: 'No results found',
      tryAgain: 'Try again',
      refresh: 'Refresh',
      more: 'More',
      less: 'Less',
      seeAll: 'See all',
      learnMore: 'Learn more',
      to: 'to',
      viewDetails: 'View details',
    },
    
    // Navigation
    nav: {
      dashboard: 'Dashboard',
      properties: 'Properties',
      units: 'Units',
      tenants: 'Tenants',
      leases: 'Leases',
      payments: 'Payments',
      expenses: 'Expenses',
      analytics: 'Analytics',
      messages: 'Messages',
      notifications: 'Notifications',
      reports: 'Reports',
      subscription: 'Subscription',
      ratings: 'My Ratings',
      support: 'Support',
      settings: 'Settings',
      profile: 'Profile',
      logout: 'Logout',
      faqs: 'FAQs',
      helpTutorial: 'Help & Tutorial',
      managers: 'Managers',
      receipts: 'Receipts',
      myLease: 'My Lease',
    },
    
    // Auth
    auth: {
      login: 'Login',
      register: 'Register',
      forgotPassword: 'Forgot Password',
      resetPassword: 'Reset Password',
      email: 'Email',
      password: 'Password',
      confirmPassword: 'Confirm Password',
      name: 'Full Name',
      phone: 'Phone Number',
      rememberMe: 'Remember Me',
      loginSuccess: 'Login successful',
      loginError: 'Invalid email or password',
      registerSuccess: 'Registration successful',
      logoutSuccess: 'Logged out successfully',
      createAccount: 'Create Account',
      alreadyHaveAccount: 'Already have an account?',
      dontHaveAccount: "Don't have an account?",
      signIn: 'Sign In',
      signUp: 'Sign Up',
      signInWithGoogle: 'Sign in with Google',
      orContinueWith: 'Or continue with',
      enterYourEmail: 'Enter your email',
      enterYourPassword: 'Enter your password',
      passwordRequirements: 'Password must be at least 6 characters',
      verifyEmail: 'Verify Email',
      checkYourEmail: 'Check your email for verification link',
      resendEmail: 'Resend verification email',
      emailVerified: 'Email verified successfully',
      landlordCode: 'Landlord Code',
      enterLandlordCode: 'Enter the 6-character code from your landlord',
      completeRegistration: 'Complete Registration',
      youHaveBeenInvited: 'You have been invited to join U-DORM as a tenant',
      hideOptionalFields: 'Hide Optional Fields',
      showOptionalFields: 'Show Optional Profile (Optional)',
      optionalFieldsDescription: 'These fields are optional but help your landlord know more about you.',
      occupationPlaceholder: 'e.g., Software Engineer',
      homeAddressPlaceholder: 'Your permanent home address',
      relationshipPlaceholder: 'e.g., Parent, Sibling',
      createPassword: 'Create Password',
      fullName: 'Full Name',
      emailPlaceholder: 'you@example.com',
      registrationSuccess: 'Registration successful! Welcome to U-DORM.',
    },
    
    // Dashboard
    dashboard: {
      welcome: 'Welcome',
      welcomeBack: 'Welcome Back',
      totalProperties: 'Total Properties',
      totalUnits: 'Total Units',
      occupiedUnits: 'Occupied Units',
      vacantUnits: 'Vacant Units',
      totalTenants: 'Total Tenants',
      activeTenants: 'Active Tenants',
      pendingTenants: 'Pending Tenants',
      monthlyRevenue: 'Monthly Revenue',
      pendingPayments: 'Pending Payments',
      recentActivity: 'Recent Activity',
      quickActions: 'Quick Actions',
      yourSubscription: 'Your Subscription',
      yourLandlordCode: 'Your Landlord Code',
      shareCodeDescription: 'Share this code with tenants during registration',
      occupancyRate: 'Occupancy Rate',
      activeLeases: 'Active Leases',
      pendingVerification: 'Pending Verification',
      unreadMessages: 'Unread Messages',
      currentResidence: 'Your Current Residence',
      thisMonthPayment: "This Month's Payment",
      leaseStatus: 'Lease Status',
      paymentSchedule: 'Payment Schedule',
      rentDueOn: 'Rent is due on the',
      ofEachMonth: 'of each month',
      payNow: 'Pay Now',
      rentalOverview: "Here's your rental overview",
      propertyOverview: "Here's your property overview",
      pendingActions: 'Pending Actions',
      noPendingActions: 'No pending actions',
    },
    
    // Properties
    properties: {
      title: 'Properties',
      addProperty: 'Add Property',
      editProperty: 'Edit Property',
      deleteProperty: 'Delete Property',
      propertyName: 'Property Name',
      address: 'Address',
      city: 'City',
      description: 'Description',
      totalUnits: 'Total Units',
      occupancy: 'Occupancy',
      createdAt: 'Created At',
      noProperties: 'No properties yet',
      addFirstProperty: 'Add your first property to get started',
      propertyDetails: 'Property Details',
      propertyImage: 'Property Image',
      uploadImage: 'Upload Image',
      changeImage: 'Change Image',
      location: 'Location',
      amenities: 'Amenities',
      nearbyPlaces: 'Nearby Places',
    },
    
    // Units
    units: {
      title: 'Units',
      room: 'Room',
      addUnit: 'Add Unit',
      editUnit: 'Edit Unit',
      deleteUnit: 'Delete Unit',
      bulkAdd: 'Bulk Add Units',
      unitNumber: 'Unit Number',
      floor: 'Floor',
      capacity: 'Capacity',
      monthlyRent: 'Monthly Rent',
      status: 'Status',
      vacant: 'Vacant',
      occupied: 'Occupied',
      maintenance: 'Under Maintenance',
      amenities: 'Amenities',
      noUnits: 'No units yet',
      unitDetails: 'Unit Details',
      currentTenant: 'Current Tenant',
      noTenant: 'No tenant assigned',
      bedrooms: 'Bedrooms',
      bathrooms: 'Bathrooms',
      size: 'Size',
      sqft: 'sq ft',
      sqm: 'sq m',
    },
    
    // Tenants
    tenants: {
      title: 'Tenants',
      addTenant: 'Add Tenant',
      editTenant: 'Edit Tenant',
      inviteTenant: 'Invite Tenant',
      verifyTenant: 'Verify Tenant',
      removeTenant: 'Remove Tenant',
      tenantName: 'Tenant Name',
      email: 'Email',
      phone: 'Phone',
      unit: 'Assigned Unit',
      leaseStatus: 'Lease Status',
      activeTenants: 'Active Tenants',
      pendingTenants: 'Pending Tenants',
      availableTenants: 'Available',
      formerTenants: 'Former Tenants',
      noTenants: 'No tenants yet',
      tenantDetails: 'Tenant Details',
      tenantProfile: 'Tenant Profile',
      birthday: 'Birthday',
      age: 'Age',
      occupation: 'Occupation',
      isStudent: 'Student',
      studentId: 'Student ID',
      schoolName: 'School/University',
      homeAddress: 'Home Address',
      emergencyContact: 'Emergency Contact',
      relationship: 'Relationship',
      moveInDate: 'Move-in Date',
      moveOutDate: 'Move-out Date',
      currentResidence: 'Your Current Residence',
      landlord: 'Landlord',
      pendingVerifications: 'Pending Tenant Verifications',
      shareCodeDescription: 'Share this code with tenants during registration so they can link to your account.',
      // New invite feature translations
      pendingInvitations: 'Pending Invitations',
      inviteNewTenant: 'Invite New Tenant',
      sendInvitation: 'Send Invitation',
      resendInvitation: 'Resend Invitation',
      cancelInvitation: 'Cancel Invitation',
      invitationSent: 'Invitation Sent',
      invitationExpired: 'Invitation Expired',
      awaitingRegistration: 'Awaiting Registration',
      inviteDescription: 'Create a pending tenant and send them an email invitation to register.',
      selectProperty: 'Select Property (Optional)',
      selectUnit: 'Select Unit (Optional)',
      inviteSuccess: 'Invitation sent successfully',
      addManualPayment: 'Add Manual Payment',
      manualPayment: 'Manual Payment',
      paymentProof: 'Payment Proof (Optional)',
      attachProof: 'Attach proof of payment',
      selectTenant: 'Select Tenant',
      paymentRecorded: 'Payment recorded successfully',
      // Manual tenant (no email) translations
      addManually: 'Add Manually',
      addManuallyDescription: 'Add a tenant without email. Useful for tenants who don\'t use email or for record-keeping.',
      optionalInfo: 'Optional Information',
      manualTenantNote: 'Tenants added manually cannot log in to the portal. You can convert them to a full account later by adding their email.',
      manualTenants: 'Manual Tenants',
      manualTenantsDescription: 'Tenants added manually without email addresses. You can convert them to full accounts later.',
      manualEntry: 'Manual Entry',
      noEmail: 'No email',
      addEmail: 'Add Email',
      addEmailTitle: 'Add Email & Send Invite',
      addEmailDescription: 'Add an email address to convert this tenant to a full account. They will receive an email to set their password.',
      convertEmailNote: 'Once converted, this tenant will receive an email to set up their password and access the tenant portal. All their existing records and payment history will be preserved.',
      sendSetupEmail: 'Send Setup Email',
    },
    
    // Leases
    leases: {
      title: 'Leases',
      createLease: 'Create Lease',
      editLease: 'Edit Lease',
      viewLease: 'View Lease',
      terminateLease: 'Terminate Lease',
      renewLease: 'Renew Lease',
      exportPDF: 'Export PDF',
      startDate: 'Start Date',
      endDate: 'End Date',
      monthlyRent: 'Monthly Rent',
      securityDeposit: 'Security Deposit',
      advanceRent: 'Advance Rent',
      totalAmount: 'Total Amount',
      status: 'Status',
      draft: 'Draft',
      pending: 'Pending',
      active: 'Active',
      terminated: 'Terminated',
      expired: 'Expired',
      leaseTerms: 'Lease Terms',
      houseRules: 'House Rules',
      paymentTerms: 'Payment Terms',
      termsOfService: 'Terms of Service',
      noLeases: 'No leases yet',
      leaseDetails: 'Lease Details',
      leaseAgreement: 'Lease Agreement',
      acknowledgement: 'Tenant Acknowledgement',
      acknowledged: 'Acknowledged',
      notAcknowledged: 'Not Acknowledged',
      duration: 'Duration',
      months: 'months',
      additionalFees: 'Additional Fees',
      bulkDelete: 'Bulk Delete',
      pendingLease: 'Pending Lease Agreement',
      pendingLeaseMessage: 'You have a lease agreement waiting for your acknowledgement at',
      viewAndAcknowledge: 'View & Acknowledge',
      leasePeriod: 'Lease Period',
      noActiveLease: 'No Active Lease',
      noActiveLeaseMessage: "You don't have an active lease yet. Your landlord will create one for you.",
      leaseStatus: 'Lease Status',
    },
    
    // Payments
    payments: {
      title: 'Payments',
      submitPayment: 'Submit Payment',
      verifyPayment: 'Verify Payment',
      rejectPayment: 'Reject Payment',
      amount: 'Amount',
      paymentType: 'Payment Type',
      paymentMethod: 'Payment Method',
      periodMonth: 'Period Month',
      proofOfPayment: 'Proof of Payment',
      receiptNumber: 'Receipt Number',
      submittedAt: 'Submitted At',
      verifiedAt: 'Verified At',
      rent: 'Rent',
      deposit: 'Deposit',
      advance: 'Advance',
      utility: 'Utility',
      other: 'Other',
      gcash: 'GCash',
      maya: 'Maya',
      bank: 'Bank Transfer',
      cash: 'Cash',
      noPayments: 'No payments',
      paymentInfo: 'Payment Information',
      whereToPay: 'Where to pay?',
      paymentHistory: 'Payment History',
      pendingPayments: 'Pending Payments',
      verifiedPayments: 'Verified Payments',
      rejectedPayments: 'Rejected Payments',
      downloadReceipt: 'Download Receipt',
      viewReceipt: 'View Receipt',
      paymentProof: 'Payment Proof',
      transactionId: 'Transaction ID',
      referenceNumber: 'Reference Number',
      schedule: 'Payment Schedule',
      payNow: 'Pay Now',
      thisMonth: "This Month's Payment",
      reviewAndVerify: 'Review and verify tenant payments',
      paymentsWillAppear: 'Payments will appear here when tenants submit them',
      verifySuccess: 'Payment verified successfully',
      rejectSuccess: 'Payment rejected',
      enterRejectionReason: 'Enter rejection reason (optional):',
    },
    
    // Reports
    reports: {
      title: 'Reports',
      comprehensiveReport: 'Comprehensive Report',
      financialSummary: 'Financial Summary',
      totalIncome: 'Total Income',
      totalExpenses: 'Total Expenses',
      netIncome: 'Net Income',
      pendingPayments: 'Pending Payments',
      paymentsReceived: 'Payments Received',
      pendingVerification: 'Pending Verification',
      tenantReport: 'Tenant Report',
      exportExcel: 'Export Excel',
      exportPDF: 'Export PDF',
      generateReport: 'Generate Report',
      dateRange: 'Date Range',
      from: 'From',
      to: 'To',
      thisMonth: 'This Month',
      lastMonth: 'Last Month',
      thisYear: 'This Year',
      custom: 'Custom',
    },
    
    // Settings
    settings: {
      title: 'Settings',
      paymentMethods: 'Payment Methods',
      leaseTerms: 'Lease Terms',
      notifications: 'Notifications',
      saveChanges: 'Save Changes',
      resetToDefaults: 'Reset to Defaults',
      accountName: 'Account Name',
      accountNumber: 'Account Number',
      bankName: 'Bank Name',
      branch: 'Branch',
      enableMethod: 'Enable this payment method',
      paymentDueDate: 'Payment Due Date',
      reminderDays: 'Reminder Days Before Due',
      emailNotifications: 'Email Notifications',
      smsNotifications: 'SMS Notifications',
      pushNotifications: 'Push Notifications',
      language: 'Language',
      theme: 'Theme',
      darkMode: 'Dark Mode',
      lightMode: 'Light Mode',
      systemDefault: 'System Default',
      scheduleType: 'Schedule Type',
      fixedDate: 'Fixed Monthly Date',
      paymentWindow: 'Payment Window',
      biMonthly: 'Bi-Monthly',
      perLease: 'Per Lease Agreement',
      configurePaymentMethods: 'Configure your payment methods and notification preferences',
      saveSettings: 'Save Settings',
      paymentInfoVisibility: 'Payment Information Visibility',
      paymentInfoVisibilityDesc: 'Payment methods you enable will be visible to your tenants when they submit payments. Make sure your account details are accurate.',
      leaseTermsSaved: 'Lease terms saved successfully',
      leaseTermsReset: 'Lease terms reset to defaults',
      confirmResetTerms: 'Are you sure you want to reset all lease terms to defaults? This cannot be undone.',
    },
    
    // Subscription
    subscription: {
      title: 'Subscription',
      currentPlan: 'Current Plan',
      upgradePlan: 'Upgrade Plan',
      downgradePlan: 'Downgrade Plan',
      features: 'Features',
      price: 'Price',
      perMonth: '/month',
      perYear: '/year',
      propertyLimit: 'Property Limit',
      unitLimit: 'Unit Limit',
      tenantLimit: 'Tenant Limit',
      subscribe: 'Subscribe',
      promoCode: 'Promo Code',
      applyCode: 'Apply Code',
      freeTrial: 'Free Trial',
      trialEndsIn: 'Trial ends in',
      days: 'days',
      unlimited: 'Unlimited',
      basic: 'Basic',
      pro: 'Pro',
      enterprise: 'Enterprise',
      choosePlan: 'Choose a Plan',
      cancelSubscription: 'Cancel Subscription',
      renewSubscription: 'Renew Subscription',
      billingHistory: 'Billing History',
    },
    
    // Messages
    messages: {
      title: 'Messages',
      newMessage: 'New Message',
      sendMessage: 'Send Message',
      subject: 'Subject',
      message: 'Message',
      attachImage: 'Attach Image',
      attachFile: 'Attach File',
      noMessages: 'No messages yet',
      conversations: 'Conversations',
      inbox: 'Inbox',
      sent: 'Sent',
      archived: 'Archived',
      markAsRead: 'Mark as Read',
      markAsUnread: 'Mark as Unread',
      deleteMessage: 'Delete Message',
      replyToMessage: 'Reply',
      forwardMessage: 'Forward',
      typeMessage: 'Type your message...',
      searchMessages: 'Search messages',
      unread: 'unread',
      checkInbox: 'Check your inbox',
      noUnread: 'No unread messages',
      allCaughtUp: "You're all caught up!",
      communicateWith: 'Communicate with your',
      selectConversation: 'Select a Conversation',
      chooseConversation: 'Choose a conversation from the list to start messaging',
      startConversation: 'Start a conversation',
      selectToMessage: 'Select to message your',
      noLeaseForMessaging: 'No active lease found. You can message your landlord once you have an active lease.',
      noTenantsYet: 'No tenants yet. Add tenants to start messaging.',
      noMessagesYet: 'No messages yet. Start the conversation!',
    },
    
    // Profile
    profile: {
      title: 'Profile',
      editProfile: 'Edit Profile',
      changePassword: 'Change Password',
      currentPassword: 'Current Password',
      newPassword: 'New Password',
      confirmNewPassword: 'Confirm New Password',
      uploadAvatar: 'Upload Avatar',
      removeAvatar: 'Remove Avatar',
      personalInfo: 'Personal Information',
      accountSettings: 'Account Settings',
      securitySettings: 'Security Settings',
      notificationPreferences: 'Notification Preferences',
      deleteAccount: 'Delete Account',
      memberSince: 'Member since',
      lastLogin: 'Last login',
      updateProfile: 'Update Profile',
      profileUpdated: 'Profile updated successfully',
    },
    
    // Landing Page
    landing: {
      heroTitle: 'Simplify Your Dormitory Management',
      heroSubtitle: 'All-in-one platform for landlords, tenants, and property managers',
      startFreeTrial: 'Get Started',
      watchDemo: 'Watch Demo',
      features: 'Features',
      pricing: 'Pricing',
      testimonials: 'Testimonials',
      contactUs: 'Contact Us',
      forLandlords: 'For Landlords',
      forTenants: 'For Tenants',
      forManagers: 'For Property Managers',
      easyPayments: 'Easy Payments',
      digitalLeases: 'Digital Leases',
      realTimeReports: 'Real-time Reports',
      secureMessaging: 'Secure Messaging',
      getStarted: 'Get Started',
      learnMore: 'Learn More',
      trustedBy: 'Trusted by',
      landlords: 'landlords',
      tenants: 'tenants',
      properties: 'properties',
    },
    
    // Errors
    errors: {
      somethingWentWrong: 'Something went wrong',
      pageNotFound: 'Page not found',
      unauthorized: 'Unauthorized access',
      forbidden: 'Access forbidden',
      serverError: 'Server error',
      networkError: 'Network error',
      pleaseLogin: 'Please login to continue',
      sessionExpired: 'Session expired',
      invalidCredentials: 'Invalid credentials',
      emailAlreadyExists: 'Email already exists',
      invalidEmail: 'Invalid email format',
      passwordTooShort: 'Password is too short',
      passwordMismatch: 'Passwords do not match',
      requiredField: 'This field is required',
      invalidFormat: 'Invalid format',
      fileTooLarge: 'File is too large',
      unsupportedFileType: 'Unsupported file type',
    },
    
    // Ratings
    ratings: {
      rateExperience: 'Rate Your Experience',
      rate: 'Rate',
      leaseEndedMessage: 'Your lease at {property} has ended. Share your experience to help future tenants!',
      multipleRatingsMessage: 'You have {count} past leases to rate. Your feedback helps future tenants!',
      submitSuccess: 'Thank you for your rating! It will be visible after admin approval.',
    },
    
    // Status translations
    status: {
      verified: 'Verified',
      pending: 'Pending',
      rejected: 'Rejected',
      active: 'Active',
      inactive: 'Inactive',
      terminated: 'Terminated',
      expired: 'Expired',
      unpaid: 'Unpaid',
      paid: 'Paid',
      draft: 'Draft',
    },
    
    // Subscription
    subscription: {
      title: 'Subscription',
      currentPlan: 'Current Plan',
      yourSubscription: 'Your Subscription',
      noActiveSubscription: 'No active subscription',
    },
  },
  
  // Tagalog translations
  tl: {
    common: {
      save: 'I-save',
      cancel: 'Kanselahin',
      delete: 'Burahin',
      edit: 'I-edit',
      add: 'Magdagdag',
      search: 'Maghanap',
      filter: 'Salain',
      loading: 'Naglo-load...',
      noData: 'Walang datos',
      confirm: 'Kumpirmahin',
      back: 'Bumalik',
      next: 'Susunod',
      submit: 'Ipasa',
      close: 'Isara',
      view: 'Tingnan',
      download: 'I-download',
      upload: 'Mag-upload',
      copy: 'Kopyahin',
      copied: 'Nakopya na!',
      success: 'Tagumpay',
      error: 'May Error',
      warning: 'Babala',
      info: 'Impormasyon',
      yes: 'Oo',
      no: 'Hindi',
      all: 'Lahat',
      active: 'Aktibo',
      pending: 'Naghihintay',
      verified: 'Na-verify',
      rejected: 'Tinanggihan',
      expired: 'Nag-expire na',
      today: 'Ngayon',
      yesterday: 'Kahapon',
      welcome: 'Maligayang pagdating',
      home: 'Home',
      about: 'Tungkol',
      contact: 'Makipag-ugnayan',
      help: 'Tulong',
      required: 'Kailangan',
      optional: 'Opsyonal',
      actions: 'Mga Aksyon',
      details: 'Mga Detalye',
      status: 'Katayuan',
      date: 'Petsa',
      time: 'Oras',
      amount: 'Halaga',
      total: 'Kabuuan',
      notes: 'Mga Tala',
      description: 'Paglalarawan',
      name: 'Pangalan',
      email: 'Email',
      phone: 'Telepono',
      address: 'Address',
      selectOption: 'Pumili ng opsyon',
      noResults: 'Walang nahanap',
      tryAgain: 'Subukan muli',
      refresh: 'I-refresh',
      more: 'Higit pa',
      less: 'Mas kaunti',
      seeAll: 'Tingnan lahat',
      learnMore: 'Alamin pa',
    },
    nav: {
      dashboard: 'Dashboard',
      properties: 'Mga Ari-arian',
      units: 'Mga Kwarto',
      tenants: 'Mga Umuupa',
      leases: 'Mga Kontrata',
      payments: 'Mga Bayad',
      expenses: 'Mga Gastos',
      analytics: 'Mga Ulat',
      messages: 'Mga Mensahe',
      notifications: 'Mga Abiso',
      reports: 'Mga Report',
      subscription: 'Subscription',
      ratings: 'Mga Rating',
      support: 'Tulong',
      settings: 'Mga Setting',
      profile: 'Profile',
      logout: 'Mag-logout',
      faqs: 'Mga Tanong',
      helpTutorial: 'Tulong at Tutorial',
      managers: 'Mga Manager',
      receipts: 'Mga Resibo',
      myLease: 'Aking Kontrata',
    },
    auth: {
      login: 'Mag-login',
      register: 'Mag-register',
      forgotPassword: 'Nakalimutan ang Password',
      resetPassword: 'I-reset ang Password',
      email: 'Email',
      password: 'Password',
      confirmPassword: 'Kumpirmahin ang Password',
      name: 'Buong Pangalan',
      phone: 'Numero ng Telepono',
      rememberMe: 'Tandaan Ako',
      loginSuccess: 'Matagumpay na naka-login',
      loginError: 'Mali ang email o password',
      registerSuccess: 'Matagumpay na nakapag-register',
      logoutSuccess: 'Matagumpay na naka-logout',
      createAccount: 'Gumawa ng Account',
      alreadyHaveAccount: 'May account ka na?',
      dontHaveAccount: 'Wala pang account?',
      signIn: 'Mag-sign in',
      signUp: 'Mag-sign up',
      signInWithGoogle: 'Mag-sign in gamit ang Google',
      orContinueWith: 'O magpatuloy gamit ang',
      enterYourEmail: 'Ilagay ang iyong email',
      enterYourPassword: 'Ilagay ang iyong password',
      passwordRequirements: 'Ang password ay dapat hindi bababa sa 6 na karakter',
      verifyEmail: 'I-verify ang Email',
      checkYourEmail: 'Tingnan ang iyong email para sa verification link',
      resendEmail: 'Ipadala muli ang verification email',
      emailVerified: 'Matagumpay na na-verify ang email',
      landlordCode: 'Landlord Code',
      enterLandlordCode: 'Ilagay ang 6-character code mula sa iyong landlord',
    },
    dashboard: {
      welcome: 'Maligayang pagdating',
      welcomeBack: 'Maligayang pagbabalik',
      totalProperties: 'Kabuuang Ari-arian',
      totalUnits: 'Kabuuang Kwarto',
      occupiedUnits: 'Mga Okupadong Kwarto',
      vacantUnits: 'Mga Bakanteng Kwarto',
      totalTenants: 'Kabuuang Umuupa',
      activeTenants: 'Mga Aktibong Umuupa',
      pendingTenants: 'Mga Naghihintay na Umuupa',
      monthlyRevenue: 'Buwanang Kita',
      pendingPayments: 'Mga Naghihintay na Bayad',
      recentActivity: 'Kamakailang Aktibidad',
      quickActions: 'Mabilis na Aksyon',
      yourSubscription: 'Iyong Subscription',
      yourLandlordCode: 'Iyong Landlord Code',
      shareCodeDescription: 'Ibahagi ang code na ito sa mga umuupa sa kanilang pagpapatala',
      occupancyRate: 'Rate ng Occupancy',
      activeLeases: 'Mga Aktibong Kontrata',
      pendingVerification: 'Naghihintay ng Verification',
      unreadMessages: 'Di-nabasa na Mensahe',
      currentResidence: 'Kasalukuyang Tirahan',
      thisMonthPayment: 'Bayad Ngayong Buwan',
      leaseStatus: 'Katayuan ng Kontrata',
      paymentSchedule: 'Iskedyul ng Bayad',
      rentDueOn: 'Ang upa ay due sa',
      ofEachMonth: 'ng bawat buwan',
      payNow: 'Magbayad Ngayon',
      rentalOverview: 'Narito ang iyong rental overview',
      propertyOverview: 'Narito ang iyong property overview',
      pendingActions: 'Mga Naghihintay na Aksyon',
      noPendingActions: 'Walang naghihintay na aksyon',
    },
    properties: {
      title: 'Mga Ari-arian',
      addProperty: 'Magdagdag ng Ari-arian',
      editProperty: 'I-edit ang Ari-arian',
      deleteProperty: 'Burahin ang Ari-arian',
      propertyName: 'Pangalan ng Ari-arian',
      address: 'Address',
      city: 'Lungsod',
      description: 'Paglalarawan',
      totalUnits: 'Kabuuang Kwarto',
      occupancy: 'Occupancy',
      createdAt: 'Ginawa Noong',
      noProperties: 'Wala pang ari-arian',
      addFirstProperty: 'Magdagdag ng unang ari-arian para magsimula',
      propertyDetails: 'Mga Detalye ng Ari-arian',
      propertyImage: 'Larawan ng Ari-arian',
      uploadImage: 'Mag-upload ng Larawan',
      changeImage: 'Palitan ang Larawan',
      location: 'Lokasyon',
      amenities: 'Mga Amenities',
      nearbyPlaces: 'Mga Malapit na Lugar',
    },
    payments: {
      title: 'Mga Bayad',
      submitPayment: 'Magpasa ng Bayad',
      verifyPayment: 'I-verify ang Bayad',
      rejectPayment: 'Tanggihan ang Bayad',
      amount: 'Halaga',
      paymentType: 'Uri ng Bayad',
      paymentMethod: 'Paraan ng Pagbabayad',
      periodMonth: 'Buwan ng Bayad',
      proofOfPayment: 'Patunay ng Bayad',
      receiptNumber: 'Numero ng Resibo',
      submittedAt: 'Ipinasa Noong',
      verifiedAt: 'Na-verify Noong',
      rent: 'Upa',
      deposit: 'Deposito',
      advance: 'Paunang Bayad',
      utility: 'Kuryente/Tubig',
      other: 'Iba pa',
      gcash: 'GCash',
      maya: 'Maya',
      bank: 'Bank Transfer',
      cash: 'Cash',
      noPayments: 'Wala pang bayad',
      paymentInfo: 'Impormasyon sa Pagbabayad',
      whereToPay: 'Saan magbabayad?',
      paymentHistory: 'Kasaysayan ng Pagbabayad',
      pendingPayments: 'Mga Naghihintay na Bayad',
      verifiedPayments: 'Mga Na-verify na Bayad',
      rejectedPayments: 'Mga Tinanggihang Bayad',
      downloadReceipt: 'I-download ang Resibo',
      viewReceipt: 'Tingnan ang Resibo',
      paymentProof: 'Patunay ng Bayad',
      transactionId: 'Transaction ID',
      referenceNumber: 'Reference Number',
    },
    settings: {
      title: 'Mga Setting',
      paymentMethods: 'Mga Paraan ng Pagbabayad',
      leaseTerms: 'Mga Tuntunin ng Kontrata',
      notifications: 'Mga Abiso',
      saveChanges: 'I-save ang mga Pagbabago',
      resetToDefaults: 'I-reset sa Default',
      accountName: 'Pangalan ng Account',
      accountNumber: 'Numero ng Account',
      bankName: 'Pangalan ng Bangko',
      branch: 'Branch',
      enableMethod: 'I-enable ang paraan ng pagbabayad na ito',
      paymentDueDate: 'Petsa ng Due ng Bayad',
      reminderDays: 'Mga Araw Bago ang Due para sa Paalala',
      emailNotifications: 'Mga Email Notification',
      smsNotifications: 'Mga SMS Notification',
      pushNotifications: 'Mga Push Notification',
      language: 'Wika',
      theme: 'Tema',
      darkMode: 'Dark Mode',
      lightMode: 'Light Mode',
      systemDefault: 'Default ng System',
      scheduleType: 'Uri ng Iskedyul',
      fixedDate: 'Fixed na Buwanang Petsa',
      paymentWindow: 'Window ng Pagbabayad',
      biMonthly: 'Dalawang Beses sa Isang Buwan',
      perLease: 'Ayon sa Kontrata',
    },
    landing: {
      heroTitle: 'Pasimplehin ang Iyong Dormitory Management',
      heroSubtitle: 'All-in-one platform para sa mga landlord, umuupa, at property manager',
      startFreeTrial: 'Mag-sign Up',
      watchDemo: 'Panoorin ang Demo',
      features: 'Mga Feature',
      pricing: 'Presyo',
      testimonials: 'Mga Testimonya',
      contactUs: 'Makipag-ugnayan',
      forLandlords: 'Para sa mga Landlord',
      forTenants: 'Para sa mga Umuupa',
      forManagers: 'Para sa mga Property Manager',
      easyPayments: 'Madaling Pagbabayad',
      digitalLeases: 'Digital na Kontrata',
      realTimeReports: 'Real-time na Report',
      secureMessaging: 'Secure na Mensahe',
      getStarted: 'Magsimula',
      learnMore: 'Alamin Pa',
      trustedBy: 'Pinagkakatiwalaan ng',
      landlords: 'landlord',
      tenants: 'umuupa',
      properties: 'ari-arian',
    },
    errors: {
      somethingWentWrong: 'May nangyaring mali',
      pageNotFound: 'Hindi mahanap ang pahina',
      unauthorized: 'Hindi awtorisadong access',
      forbidden: 'Bawal ang access',
      serverError: 'Error sa server',
      networkError: 'Error sa network',
      pleaseLogin: 'Mag-login upang magpatuloy',
      sessionExpired: 'Nag-expire na ang session',
      invalidCredentials: 'Hindi valid ang credentials',
      emailAlreadyExists: 'Mayroon nang email na ito',
      invalidEmail: 'Hindi valid ang format ng email',
      passwordTooShort: 'Masyadong maikli ang password',
      passwordMismatch: 'Hindi magkatugma ang mga password',
      requiredField: 'Kailangan ang field na ito',
      invalidFormat: 'Hindi valid ang format',
      fileTooLarge: 'Masyadong malaki ang file',
      unsupportedFileType: 'Hindi suportadong uri ng file',
    },
  },
  
  // Spanish translations
  es: {
    common: {
      save: 'Guardar',
      cancel: 'Cancelar',
      delete: 'Eliminar',
      edit: 'Editar',
      add: 'Agregar',
      search: 'Buscar',
      filter: 'Filtrar',
      loading: 'Cargando...',
      noData: 'Sin datos disponibles',
      confirm: 'Confirmar',
      back: 'Atrás',
      next: 'Siguiente',
      submit: 'Enviar',
      close: 'Cerrar',
      view: 'Ver',
      download: 'Descargar',
      upload: 'Subir',
      copy: 'Copiar',
      copied: '¡Copiado!',
      success: 'Éxito',
      error: 'Error',
      warning: 'Advertencia',
      info: 'Información',
      yes: 'Sí',
      no: 'No',
      all: 'Todo',
      active: 'Activo',
      pending: 'Pendiente',
      verified: 'Verificado',
      rejected: 'Rechazado',
      expired: 'Expirado',
      today: 'Hoy',
      yesterday: 'Ayer',
      welcome: 'Bienvenido',
      home: 'Inicio',
      about: 'Acerca de',
      contact: 'Contacto',
      help: 'Ayuda',
      required: 'Obligatorio',
      optional: 'Opcional',
      actions: 'Acciones',
      details: 'Detalles',
      status: 'Estado',
      date: 'Fecha',
      time: 'Hora',
      amount: 'Cantidad',
      total: 'Total',
      notes: 'Notas',
      description: 'Descripción',
      name: 'Nombre',
      email: 'Correo',
      phone: 'Teléfono',
      address: 'Dirección',
      selectOption: 'Seleccionar opción',
      noResults: 'Sin resultados',
      tryAgain: 'Intentar de nuevo',
      refresh: 'Actualizar',
      more: 'Más',
      less: 'Menos',
      seeAll: 'Ver todo',
      learnMore: 'Más información',
    },
    nav: {
      dashboard: 'Panel',
      properties: 'Propiedades',
      units: 'Unidades',
      tenants: 'Inquilinos',
      leases: 'Contratos',
      payments: 'Pagos',
      expenses: 'Gastos',
      analytics: 'Análisis',
      messages: 'Mensajes',
      notifications: 'Notificaciones',
      reports: 'Informes',
      subscription: 'Suscripción',
      ratings: 'Calificaciones',
      support: 'Soporte',
      settings: 'Configuración',
      profile: 'Perfil',
      logout: 'Cerrar sesión',
      faqs: 'Preguntas frecuentes',
      helpTutorial: 'Ayuda y Tutorial',
      managers: 'Administradores',
      receipts: 'Recibos',
      myLease: 'Mi Contrato',
    },
    auth: {
      login: 'Iniciar sesión',
      register: 'Registrarse',
      forgotPassword: 'Olvidé mi contraseña',
      resetPassword: 'Restablecer contraseña',
      email: 'Correo electrónico',
      password: 'Contraseña',
      confirmPassword: 'Confirmar contraseña',
      name: 'Nombre completo',
      phone: 'Número de teléfono',
      rememberMe: 'Recordarme',
      loginSuccess: 'Inicio de sesión exitoso',
      loginError: 'Correo o contraseña inválidos',
      registerSuccess: 'Registro exitoso',
      logoutSuccess: 'Sesión cerrada exitosamente',
      createAccount: 'Crear cuenta',
      alreadyHaveAccount: '¿Ya tienes cuenta?',
      dontHaveAccount: '¿No tienes cuenta?',
      signIn: 'Entrar',
      signUp: 'Registrarse',
      signInWithGoogle: 'Iniciar sesión con Google',
      orContinueWith: 'O continuar con',
      enterYourEmail: 'Ingresa tu correo',
      enterYourPassword: 'Ingresa tu contraseña',
      passwordRequirements: 'La contraseña debe tener al menos 6 caracteres',
      verifyEmail: 'Verificar correo',
      checkYourEmail: 'Revisa tu correo para el enlace de verificación',
      resendEmail: 'Reenviar correo de verificación',
      emailVerified: 'Correo verificado exitosamente',
      landlordCode: 'Código de arrendador',
      enterLandlordCode: 'Ingresa el código de 6 caracteres de tu arrendador',
    },
    dashboard: {
      welcome: 'Bienvenido',
      welcomeBack: 'Bienvenido de nuevo',
      totalProperties: 'Total de propiedades',
      totalUnits: 'Total de unidades',
      occupiedUnits: 'Unidades ocupadas',
      vacantUnits: 'Unidades vacantes',
      totalTenants: 'Total de inquilinos',
      activeTenants: 'Inquilinos activos',
      pendingTenants: 'Inquilinos pendientes',
      monthlyRevenue: 'Ingresos mensuales',
      pendingPayments: 'Pagos pendientes',
      recentActivity: 'Actividad reciente',
      quickActions: 'Acciones rápidas',
      yourSubscription: 'Tu suscripción',
      yourLandlordCode: 'Tu código de arrendador',
      shareCodeDescription: 'Comparte este código con los inquilinos durante el registro',
      occupancyRate: 'Tasa de ocupación',
      activeLeases: 'Contratos activos',
      pendingVerification: 'Verificación pendiente',
      unreadMessages: 'Mensajes sin leer',
      currentResidence: 'Tu residencia actual',
      thisMonthPayment: 'Pago de este mes',
      leaseStatus: 'Estado del contrato',
      paymentSchedule: 'Calendario de pagos',
      rentDueOn: 'El alquiler vence el',
      ofEachMonth: 'de cada mes',
      payNow: 'Pagar ahora',
      rentalOverview: 'Aquí está tu resumen de alquiler',
      propertyOverview: 'Aquí está tu resumen de propiedades',
      pendingActions: 'Acciones pendientes',
      noPendingActions: 'Sin acciones pendientes',
    },
    payments: {
      title: 'Pagos',
      submitPayment: 'Enviar pago',
      verifyPayment: 'Verificar pago',
      rejectPayment: 'Rechazar pago',
      amount: 'Cantidad',
      paymentType: 'Tipo de pago',
      paymentMethod: 'Método de pago',
      periodMonth: 'Mes del período',
      proofOfPayment: 'Comprobante de pago',
      receiptNumber: 'Número de recibo',
      submittedAt: 'Enviado el',
      verifiedAt: 'Verificado el',
      rent: 'Alquiler',
      deposit: 'Depósito',
      advance: 'Anticipo',
      utility: 'Servicios',
      other: 'Otro',
      gcash: 'GCash',
      maya: 'Maya',
      bank: 'Transferencia bancaria',
      cash: 'Efectivo',
      noPayments: 'Sin pagos aún',
      paymentInfo: 'Información de pago',
      whereToPay: '¿Dónde pagar?',
      paymentHistory: 'Historial de pagos',
      pendingPayments: 'Pagos pendientes',
      verifiedPayments: 'Pagos verificados',
      rejectedPayments: 'Pagos rechazados',
      downloadReceipt: 'Descargar recibo',
      viewReceipt: 'Ver recibo',
      paymentProof: 'Comprobante de pago',
      transactionId: 'ID de transacción',
      referenceNumber: 'Número de referencia',
    },
    settings: {
      title: 'Configuración',
      paymentMethods: 'Métodos de pago',
      leaseTerms: 'Términos del contrato',
      notifications: 'Notificaciones',
      saveChanges: 'Guardar cambios',
      resetToDefaults: 'Restablecer valores',
      accountName: 'Nombre de cuenta',
      accountNumber: 'Número de cuenta',
      bankName: 'Nombre del banco',
      branch: 'Sucursal',
      enableMethod: 'Habilitar este método de pago',
      paymentDueDate: 'Fecha de vencimiento',
      reminderDays: 'Días de recordatorio antes del vencimiento',
      emailNotifications: 'Notificaciones por correo',
      smsNotifications: 'Notificaciones por SMS',
      pushNotifications: 'Notificaciones push',
      language: 'Idioma',
      theme: 'Tema',
      darkMode: 'Modo oscuro',
      lightMode: 'Modo claro',
      systemDefault: 'Predeterminado del sistema',
      scheduleType: 'Tipo de calendario',
      fixedDate: 'Fecha mensual fija',
      paymentWindow: 'Ventana de pago',
      biMonthly: 'Quincenal',
      perLease: 'Por contrato',
    },
    landing: {
      heroTitle: 'Simplifica la Gestión de tu Dormitorio',
      heroSubtitle: 'Plataforma todo en uno para arrendadores, inquilinos y administradores',
      startFreeTrial: 'Comenzar',
      watchDemo: 'Ver demostración',
      features: 'Características',
      pricing: 'Precios',
      testimonials: 'Testimonios',
      contactUs: 'Contáctanos',
      forLandlords: 'Para arrendadores',
      forTenants: 'Para inquilinos',
      forManagers: 'Para administradores',
      easyPayments: 'Pagos fáciles',
      digitalLeases: 'Contratos digitales',
      realTimeReports: 'Informes en tiempo real',
      secureMessaging: 'Mensajería segura',
      getStarted: 'Comenzar',
      learnMore: 'Más información',
      trustedBy: 'Con la confianza de',
      landlords: 'arrendadores',
      tenants: 'inquilinos',
      properties: 'propiedades',
    },
    errors: {
      somethingWentWrong: 'Algo salió mal',
      pageNotFound: 'Página no encontrada',
      unauthorized: 'Acceso no autorizado',
      forbidden: 'Acceso prohibido',
      serverError: 'Error del servidor',
      networkError: 'Error de red',
      pleaseLogin: 'Por favor inicia sesión',
      sessionExpired: 'Sesión expirada',
      invalidCredentials: 'Credenciales inválidas',
      emailAlreadyExists: 'El correo ya existe',
      invalidEmail: 'Formato de correo inválido',
      passwordTooShort: 'Contraseña muy corta',
      passwordMismatch: 'Las contraseñas no coinciden',
      requiredField: 'Campo obligatorio',
      invalidFormat: 'Formato inválido',
      fileTooLarge: 'Archivo muy grande',
      unsupportedFileType: 'Tipo de archivo no soportado',
    },
  },
  
  // French translations
  fr: {
    common: {
      save: 'Enregistrer',
      cancel: 'Annuler',
      delete: 'Supprimer',
      edit: 'Modifier',
      add: 'Ajouter',
      search: 'Rechercher',
      filter: 'Filtrer',
      loading: 'Chargement...',
      noData: 'Aucune donnée disponible',
      confirm: 'Confirmer',
      back: 'Retour',
      next: 'Suivant',
      submit: 'Soumettre',
      close: 'Fermer',
      view: 'Voir',
      download: 'Télécharger',
      upload: 'Téléverser',
      copy: 'Copier',
      copied: 'Copié !',
      success: 'Succès',
      error: 'Erreur',
      warning: 'Avertissement',
      info: 'Information',
      yes: 'Oui',
      no: 'Non',
      all: 'Tout',
      active: 'Actif',
      pending: 'En attente',
      verified: 'Vérifié',
      rejected: 'Rejeté',
      expired: 'Expiré',
      today: "Aujourd'hui",
      yesterday: 'Hier',
      welcome: 'Bienvenue',
      home: 'Accueil',
      about: 'À propos',
      contact: 'Contact',
      help: 'Aide',
      required: 'Obligatoire',
      optional: 'Optionnel',
      actions: 'Actions',
      details: 'Détails',
      status: 'Statut',
      date: 'Date',
      time: 'Heure',
      amount: 'Montant',
      total: 'Total',
      notes: 'Notes',
      description: 'Description',
      name: 'Nom',
      email: 'E-mail',
      phone: 'Téléphone',
      address: 'Adresse',
      selectOption: 'Sélectionner une option',
      noResults: 'Aucun résultat',
      tryAgain: 'Réessayer',
      refresh: 'Actualiser',
      more: 'Plus',
      less: 'Moins',
      seeAll: 'Voir tout',
      learnMore: 'En savoir plus',
    },
    nav: {
      dashboard: 'Tableau de bord',
      properties: 'Propriétés',
      units: 'Unités',
      tenants: 'Locataires',
      leases: 'Baux',
      payments: 'Paiements',
      expenses: 'Dépenses',
      analytics: 'Analyses',
      messages: 'Messages',
      notifications: 'Notifications',
      reports: 'Rapports',
      subscription: 'Abonnement',
      ratings: 'Évaluations',
      support: 'Support',
      settings: 'Paramètres',
      profile: 'Profil',
      logout: 'Déconnexion',
      faqs: 'FAQ',
      helpTutorial: 'Aide et Tutoriel',
      managers: 'Gestionnaires',
      receipts: 'Reçus',
      myLease: 'Mon bail',
    },
    auth: {
      login: 'Connexion',
      register: "S'inscrire",
      forgotPassword: 'Mot de passe oublié',
      resetPassword: 'Réinitialiser le mot de passe',
      email: 'E-mail',
      password: 'Mot de passe',
      confirmPassword: 'Confirmer le mot de passe',
      name: 'Nom complet',
      phone: 'Numéro de téléphone',
      rememberMe: 'Se souvenir de moi',
      loginSuccess: 'Connexion réussie',
      loginError: 'E-mail ou mot de passe invalide',
      registerSuccess: 'Inscription réussie',
      logoutSuccess: 'Déconnexion réussie',
      createAccount: 'Créer un compte',
      alreadyHaveAccount: 'Vous avez déjà un compte ?',
      dontHaveAccount: "Vous n'avez pas de compte ?",
      signIn: 'Se connecter',
      signUp: "S'inscrire",
      signInWithGoogle: 'Se connecter avec Google',
      orContinueWith: 'Ou continuer avec',
      enterYourEmail: 'Entrez votre e-mail',
      enterYourPassword: 'Entrez votre mot de passe',
      passwordRequirements: 'Le mot de passe doit contenir au moins 6 caractères',
      verifyEmail: "Vérifier l'e-mail",
      checkYourEmail: 'Vérifiez votre e-mail pour le lien de vérification',
      resendEmail: "Renvoyer l'e-mail de vérification",
      emailVerified: 'E-mail vérifié avec succès',
      landlordCode: 'Code propriétaire',
      enterLandlordCode: 'Entrez le code à 6 caractères de votre propriétaire',
    },
    dashboard: {
      welcome: 'Bienvenue',
      welcomeBack: 'Bon retour',
      totalProperties: 'Total des propriétés',
      totalUnits: 'Total des unités',
      occupiedUnits: 'Unités occupées',
      vacantUnits: 'Unités vacantes',
      totalTenants: 'Total des locataires',
      activeTenants: 'Locataires actifs',
      pendingTenants: 'Locataires en attente',
      monthlyRevenue: 'Revenus mensuels',
      pendingPayments: 'Paiements en attente',
      recentActivity: 'Activité récente',
      quickActions: 'Actions rapides',
      yourSubscription: 'Votre abonnement',
      yourLandlordCode: 'Votre code propriétaire',
      shareCodeDescription: "Partagez ce code avec les locataires lors de l'inscription",
      occupancyRate: "Taux d'occupation",
      activeLeases: 'Baux actifs',
      pendingVerification: 'Vérification en attente',
      unreadMessages: 'Messages non lus',
      currentResidence: 'Votre résidence actuelle',
      thisMonthPayment: 'Paiement de ce mois',
      leaseStatus: 'Statut du bail',
      paymentSchedule: 'Calendrier des paiements',
      rentDueOn: "Le loyer est dû le",
      ofEachMonth: 'de chaque mois',
      payNow: 'Payer maintenant',
      rentalOverview: 'Voici votre aperçu de location',
      propertyOverview: 'Voici votre aperçu des propriétés',
      pendingActions: 'Actions en attente',
      noPendingActions: 'Aucune action en attente',
    },
    payments: {
      title: 'Paiements',
      submitPayment: 'Soumettre le paiement',
      verifyPayment: 'Vérifier le paiement',
      rejectPayment: 'Rejeter le paiement',
      amount: 'Montant',
      paymentType: 'Type de paiement',
      paymentMethod: 'Méthode de paiement',
      periodMonth: 'Mois de la période',
      proofOfPayment: 'Preuve de paiement',
      receiptNumber: 'Numéro de reçu',
      submittedAt: 'Soumis le',
      verifiedAt: 'Vérifié le',
      rent: 'Loyer',
      deposit: 'Dépôt',
      advance: 'Avance',
      utility: 'Services',
      other: 'Autre',
      gcash: 'GCash',
      maya: 'Maya',
      bank: 'Virement bancaire',
      cash: 'Espèces',
      noPayments: 'Aucun paiement encore',
      paymentInfo: 'Informations de paiement',
      whereToPay: 'Où payer ?',
      paymentHistory: 'Historique des paiements',
      pendingPayments: 'Paiements en attente',
      verifiedPayments: 'Paiements vérifiés',
      rejectedPayments: 'Paiements rejetés',
      downloadReceipt: 'Télécharger le reçu',
      viewReceipt: 'Voir le reçu',
      paymentProof: 'Preuve de paiement',
      transactionId: 'ID de transaction',
      referenceNumber: 'Numéro de référence',
    },
    settings: {
      title: 'Paramètres',
      paymentMethods: 'Méthodes de paiement',
      leaseTerms: 'Conditions du bail',
      notifications: 'Notifications',
      saveChanges: 'Enregistrer les modifications',
      resetToDefaults: 'Réinitialiser par défaut',
      accountName: 'Nom du compte',
      accountNumber: 'Numéro de compte',
      bankName: 'Nom de la banque',
      branch: 'Agence',
      enableMethod: 'Activer cette méthode de paiement',
      paymentDueDate: "Date d'échéance",
      reminderDays: "Jours de rappel avant l'échéance",
      emailNotifications: 'Notifications par e-mail',
      smsNotifications: 'Notifications par SMS',
      pushNotifications: 'Notifications push',
      language: 'Langue',
      theme: 'Thème',
      darkMode: 'Mode sombre',
      lightMode: 'Mode clair',
      systemDefault: 'Par défaut du système',
      scheduleType: 'Type de calendrier',
      fixedDate: 'Date mensuelle fixe',
      paymentWindow: 'Fenêtre de paiement',
      biMonthly: 'Bimensuel',
      perLease: 'Par bail',
    },
    landing: {
      heroTitle: 'Simplifiez la Gestion de Votre Dortoir',
      heroSubtitle: 'Plateforme tout-en-un pour propriétaires, locataires et gestionnaires',
      startFreeTrial: 'Commencer',
      watchDemo: 'Voir la démo',
      features: 'Fonctionnalités',
      pricing: 'Tarifs',
      testimonials: 'Témoignages',
      contactUs: 'Contactez-nous',
      forLandlords: 'Pour les propriétaires',
      forTenants: 'Pour les locataires',
      forManagers: 'Pour les gestionnaires',
      easyPayments: 'Paiements faciles',
      digitalLeases: 'Baux numériques',
      realTimeReports: 'Rapports en temps réel',
      secureMessaging: 'Messagerie sécurisée',
      getStarted: 'Commencer',
      learnMore: 'En savoir plus',
      trustedBy: 'Fait confiance par',
      landlords: 'propriétaires',
      tenants: 'locataires',
      properties: 'propriétés',
    },
    errors: {
      somethingWentWrong: "Quelque chose s'est mal passé",
      pageNotFound: 'Page non trouvée',
      unauthorized: 'Accès non autorisé',
      forbidden: 'Accès interdit',
      serverError: 'Erreur serveur',
      networkError: 'Erreur réseau',
      pleaseLogin: 'Veuillez vous connecter',
      sessionExpired: 'Session expirée',
      invalidCredentials: 'Identifiants invalides',
      emailAlreadyExists: "L'e-mail existe déjà",
      invalidEmail: "Format d'e-mail invalide",
      passwordTooShort: 'Mot de passe trop court',
      passwordMismatch: 'Les mots de passe ne correspondent pas',
      requiredField: 'Champ obligatoire',
      invalidFormat: 'Format invalide',
      fileTooLarge: 'Fichier trop volumineux',
      unsupportedFileType: 'Type de fichier non supporté',
    },
  },
  
  // Hindi translations
  hi: {
    common: {
      save: 'सहेजें',
      cancel: 'रद्द करें',
      delete: 'हटाएं',
      edit: 'संपादित करें',
      add: 'जोड़ें',
      search: 'खोजें',
      filter: 'फ़िल्टर',
      loading: 'लोड हो रहा है...',
      noData: 'कोई डेटा उपलब्ध नहीं',
      confirm: 'पुष्टि करें',
      back: 'वापस',
      next: 'अगला',
      submit: 'सबमिट करें',
      close: 'बंद करें',
      view: 'देखें',
      download: 'डाउनलोड करें',
      upload: 'अपलोड करें',
      copy: 'कॉपी करें',
      copied: 'कॉपी हो गया!',
      success: 'सफलता',
      error: 'त्रुटि',
      warning: 'चेतावनी',
      info: 'जानकारी',
      yes: 'हाँ',
      no: 'नहीं',
      all: 'सभी',
      active: 'सक्रिय',
      pending: 'लंबित',
      verified: 'सत्यापित',
      rejected: 'अस्वीकृत',
      expired: 'समाप्त',
      today: 'आज',
      yesterday: 'कल',
      welcome: 'स्वागत है',
      home: 'होम',
      about: 'के बारे में',
      contact: 'संपर्क',
      help: 'मदद',
      required: 'आवश्यक',
      optional: 'वैकल्पिक',
      actions: 'कार्य',
      details: 'विवरण',
      status: 'स्थिति',
      date: 'तारीख',
      time: 'समय',
      amount: 'राशि',
      total: 'कुल',
      notes: 'टिप्पणियां',
      description: 'विवरण',
      name: 'नाम',
      email: 'ईमेल',
      phone: 'फोन',
      address: 'पता',
      selectOption: 'विकल्प चुनें',
      noResults: 'कोई परिणाम नहीं',
      tryAgain: 'पुनः प्रयास करें',
      refresh: 'रीफ्रेश करें',
      more: 'और',
      less: 'कम',
      seeAll: 'सभी देखें',
      learnMore: 'और जानें',
    },
    nav: {
      dashboard: 'डैशबोर्ड',
      properties: 'संपत्तियां',
      units: 'यूनिट',
      tenants: 'किरायेदार',
      leases: 'पट्टे',
      payments: 'भुगतान',
      expenses: 'खर्च',
      analytics: 'विश्लेषण',
      messages: 'संदेश',
      notifications: 'सूचनाएं',
      reports: 'रिपोर्ट',
      subscription: 'सदस्यता',
      ratings: 'रेटिंग',
      support: 'सहायता',
      settings: 'सेटिंग्स',
      profile: 'प्रोफ़ाइल',
      logout: 'लॉगआउट',
      faqs: 'सवाल-जवाब',
      helpTutorial: 'मदद और ट्यूटोरियल',
      managers: 'प्रबंधक',
      receipts: 'रसीदें',
      myLease: 'मेरा पट्टा',
    },
    auth: {
      login: 'लॉगिन',
      register: 'पंजीकरण',
      forgotPassword: 'पासवर्ड भूल गए',
      resetPassword: 'पासवर्ड रीसेट करें',
      email: 'ईमेल',
      password: 'पासवर्ड',
      confirmPassword: 'पासवर्ड की पुष्टि करें',
      name: 'पूरा नाम',
      phone: 'फोन नंबर',
      rememberMe: 'मुझे याद रखें',
      loginSuccess: 'लॉगिन सफल',
      loginError: 'अमान्य ईमेल या पासवर्ड',
      registerSuccess: 'पंजीकरण सफल',
      logoutSuccess: 'लॉगआउट सफल',
      createAccount: 'खाता बनाएं',
      alreadyHaveAccount: 'पहले से खाता है?',
      dontHaveAccount: 'खाता नहीं है?',
      signIn: 'साइन इन',
      signUp: 'साइन अप',
      signInWithGoogle: 'Google से साइन इन करें',
      orContinueWith: 'या इसके साथ जारी रखें',
      enterYourEmail: 'अपना ईमेल दर्ज करें',
      enterYourPassword: 'अपना पासवर्ड दर्ज करें',
      passwordRequirements: 'पासवर्ड कम से कम 6 अक्षरों का होना चाहिए',
      verifyEmail: 'ईमेल सत्यापित करें',
      checkYourEmail: 'सत्यापन लिंक के लिए अपना ईमेल जांचें',
      resendEmail: 'सत्यापन ईमेल पुनः भेजें',
      emailVerified: 'ईमेल सफलतापूर्वक सत्यापित',
      landlordCode: 'मकान मालिक कोड',
      enterLandlordCode: 'अपने मकान मालिक का 6 अक्षरों का कोड दर्ज करें',
    },
    dashboard: {
      welcome: 'स्वागत है',
      welcomeBack: 'वापसी पर स्वागत है',
      totalProperties: 'कुल संपत्तियां',
      totalUnits: 'कुल यूनिट',
      occupiedUnits: 'भरे हुए यूनिट',
      vacantUnits: 'खाली यूनिट',
      totalTenants: 'कुल किरायेदार',
      activeTenants: 'सक्रिय किरायेदार',
      pendingTenants: 'लंबित किरायेदार',
      monthlyRevenue: 'मासिक आय',
      pendingPayments: 'लंबित भुगतान',
      recentActivity: 'हालिया गतिविधि',
      quickActions: 'त्वरित कार्य',
      yourSubscription: 'आपकी सदस्यता',
      yourLandlordCode: 'आपका मकान मालिक कोड',
      shareCodeDescription: 'पंजीकरण के दौरान किरायेदारों के साथ यह कोड साझा करें',
      occupancyRate: 'अधिभोग दर',
      activeLeases: 'सक्रिय पट्टे',
      pendingVerification: 'सत्यापन लंबित',
      unreadMessages: 'अपठित संदेश',
      currentResidence: 'आपका वर्तमान निवास',
      thisMonthPayment: 'इस महीने का भुगतान',
      leaseStatus: 'पट्टे की स्थिति',
      paymentSchedule: 'भुगतान अनुसूची',
      rentDueOn: 'किराया देय है',
      ofEachMonth: 'हर महीने की',
      payNow: 'अभी भुगतान करें',
      rentalOverview: 'यहाँ आपका किराया अवलोकन है',
      propertyOverview: 'यहाँ आपका संपत्ति अवलोकन है',
      pendingActions: 'लंबित कार्य',
      noPendingActions: 'कोई लंबित कार्य नहीं',
    },
    payments: {
      title: 'भुगतान',
      submitPayment: 'भुगतान जमा करें',
      verifyPayment: 'भुगतान सत्यापित करें',
      rejectPayment: 'भुगतान अस्वीकार करें',
      amount: 'राशि',
      paymentType: 'भुगतान प्रकार',
      paymentMethod: 'भुगतान विधि',
      periodMonth: 'अवधि का महीना',
      proofOfPayment: 'भुगतान का प्रमाण',
      receiptNumber: 'रसीद नंबर',
      submittedAt: 'जमा किया गया',
      verifiedAt: 'सत्यापित किया गया',
      rent: 'किराया',
      deposit: 'जमा',
      advance: 'अग्रिम',
      utility: 'उपयोगिता',
      other: 'अन्य',
      gcash: 'GCash',
      maya: 'Maya',
      bank: 'बैंक ट्रांसफर',
      cash: 'नकद',
      noPayments: 'अभी कोई भुगतान नहीं',
      paymentInfo: 'भुगतान जानकारी',
      whereToPay: 'कहां भुगतान करें?',
      paymentHistory: 'भुगतान इतिहास',
      pendingPayments: 'लंबित भुगतान',
      verifiedPayments: 'सत्यापित भुगतान',
      rejectedPayments: 'अस्वीकृत भुगतान',
      downloadReceipt: 'रसीद डाउनलोड करें',
      viewReceipt: 'रसीद देखें',
      paymentProof: 'भुगतान प्रमाण',
      transactionId: 'लेनदेन आईडी',
      referenceNumber: 'संदर्भ संख्या',
    },
    settings: {
      title: 'सेटिंग्स',
      paymentMethods: 'भुगतान विधियां',
      leaseTerms: 'पट्टे की शर्तें',
      notifications: 'सूचनाएं',
      saveChanges: 'परिवर्तन सहेजें',
      resetToDefaults: 'डिफ़ॉल्ट पर रीसेट करें',
      accountName: 'खाता नाम',
      accountNumber: 'खाता संख्या',
      bankName: 'बैंक का नाम',
      branch: 'शाखा',
      enableMethod: 'यह भुगतान विधि सक्षम करें',
      paymentDueDate: 'भुगतान की नियत तारीख',
      reminderDays: 'नियत तारीख से पहले रिमाइंडर दिन',
      emailNotifications: 'ईमेल सूचनाएं',
      smsNotifications: 'SMS सूचनाएं',
      pushNotifications: 'पुश सूचनाएं',
      language: 'भाषा',
      theme: 'थीम',
      darkMode: 'डार्क मोड',
      lightMode: 'लाइट मोड',
      systemDefault: 'सिस्टम डिफ़ॉल्ट',
      scheduleType: 'अनुसूची प्रकार',
      fixedDate: 'निश्चित मासिक तारीख',
      paymentWindow: 'भुगतान विंडो',
      biMonthly: 'द्वि-मासिक',
      perLease: 'प्रति पट्टा',
    },
    landing: {
      heroTitle: 'अपने छात्रावास प्रबंधन को सरल बनाएं',
      heroSubtitle: 'मकान मालिकों, किरायेदारों और संपत्ति प्रबंधकों के लिए ऑल-इन-वन प्लेटफॉर्म',
      startFreeTrial: 'शुरू करें',
      watchDemo: 'डेमो देखें',
      features: 'विशेषताएं',
      pricing: 'मूल्य निर्धारण',
      testimonials: 'प्रशंसापत्र',
      contactUs: 'संपर्क करें',
      forLandlords: 'मकान मालिकों के लिए',
      forTenants: 'किरायेदारों के लिए',
      forManagers: 'संपत्ति प्रबंधकों के लिए',
      easyPayments: 'आसान भुगतान',
      digitalLeases: 'डिजिटल पट्टे',
      realTimeReports: 'रीयल-टाइम रिपोर्ट',
      secureMessaging: 'सुरक्षित संदेश',
      getStarted: 'शुरू करें',
      learnMore: 'और जानें',
      trustedBy: 'द्वारा विश्वसनीय',
      landlords: 'मकान मालिक',
      tenants: 'किरायेदार',
      properties: 'संपत्तियां',
    },
    errors: {
      somethingWentWrong: 'कुछ गलत हो गया',
      pageNotFound: 'पेज नहीं मिला',
      unauthorized: 'अनधिकृत पहुंच',
      forbidden: 'पहुंच निषिद्ध',
      serverError: 'सर्वर त्रुटि',
      networkError: 'नेटवर्क त्रुटि',
      pleaseLogin: 'कृपया लॉगिन करें',
      sessionExpired: 'सत्र समाप्त',
      invalidCredentials: 'अमान्य क्रेडेंशियल',
      emailAlreadyExists: 'ईमेल पहले से मौजूद है',
      invalidEmail: 'अमान्य ईमेल प्रारूप',
      passwordTooShort: 'पासवर्ड बहुत छोटा है',
      passwordMismatch: 'पासवर्ड मेल नहीं खाते',
      requiredField: 'यह फ़ील्ड आवश्यक है',
      invalidFormat: 'अमान्य प्रारूप',
      fileTooLarge: 'फ़ाइल बहुत बड़ी है',
      unsupportedFileType: 'असमर्थित फ़ाइल प्रकार',
    },
  },
};

// Get current language from localStorage or default to 'en'
export const getCurrentLanguage = () => {
  if (typeof window !== 'undefined') {
    return localStorage.getItem('udorm_language') || 'en';
  }
  return 'en';
};

// Set language in localStorage
export const setLanguage = (lang) => {
  if (typeof window !== 'undefined') {
    localStorage.setItem('udorm_language', lang);
    window.dispatchEvent(new Event('languagechange'));
  }
};

// Get translation function
export const t = (key, lang = null) => {
  const currentLang = lang || getCurrentLanguage();
  const keys = key.split('.');
  
  let value = translations[currentLang];
  for (const k of keys) {
    if (value && value[k]) {
      value = value[k];
    } else {
      // Fallback to English
      value = translations.en;
      for (const k2 of keys) {
        if (value && value[k2]) {
          value = value[k2];
        } else {
          return key; // Return key if not found
        }
      }
      break;
    }
  }
  
  return typeof value === 'string' ? value : key;
};

// Available languages
export const languages = [
  { code: 'en', name: 'English', flag: '🇺🇸', nativeName: 'English' },
  { code: 'tl', name: 'Tagalog', flag: '🇵🇭', nativeName: 'Tagalog' },
  { code: 'es', name: 'Spanish', flag: '🇪🇸', nativeName: 'Español' },
  { code: 'fr', name: 'French', flag: '🇫🇷', nativeName: 'Français' },
  { code: 'hi', name: 'Hindi', flag: '🇮🇳', nativeName: 'हिन्दी' },
];

export default { translations, t, getCurrentLanguage, setLanguage, languages };
